import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Dev-only focal point editor for a portrait image.
 *
 * - Two breakpoints: "mobile" (< md) and "desktop" (>= md).
 * - Values stored in localStorage and applied via CSS variables.
 * - Editor is hidden by default. Enable by visiting the page with `?focus=edit`
 *   in the URL (or by setting `localStorage["focus-editor"] = "1"`).
 *   Visitors will never see it.
 */

type Breakpoint = "mobile" | "desktop";

interface Focal {
  x: number; // 0-100 (% from left)
  y: number; // 0-100 (% from top)
}

interface StoredState {
  values: Record<Breakpoint, Focal>;
  // When true for a breakpoint, the focal is auto-derived (from face detection
  // or defaults) and should be re-derived on load. When false, the user has
  // dragged a custom value and we respect it.
  auto: Record<Breakpoint, boolean>;
}

const STORAGE_KEY = "focal-point:david-portrait";
const ENABLE_KEY = "focus-editor";

const DEFAULTS: Record<Breakpoint, Focal> = {
  mobile: { x: 50, y: 5 },
  desktop: { x: 50, y: 8 },
};

const INITIAL_STATE: StoredState = {
  values: DEFAULTS,
  auto: { mobile: true, desktop: true },
};

function loadState(): StoredState {
  if (typeof window === "undefined") return INITIAL_STATE;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return INITIAL_STATE;
    const parsed = JSON.parse(raw);
    // Backwards-compat: older versions stored just the values object.
    if (parsed && parsed.values && parsed.auto) {
      return {
        values: {
          mobile: { ...DEFAULTS.mobile, ...parsed.values.mobile },
          desktop: { ...DEFAULTS.desktop, ...parsed.values.desktop },
        },
        auto: {
          mobile: !!parsed.auto.mobile,
          desktop: !!parsed.auto.desktop,
        },
      };
    }
    return {
      values: {
        mobile: { ...DEFAULTS.mobile, ...parsed.mobile },
        desktop: { ...DEFAULTS.desktop, ...parsed.desktop },
      },
      // Legacy values were user-set, treat as non-auto so we don't override them.
      auto: { mobile: false, desktop: false },
    };
  } catch {
    return INITIAL_STATE;
  }
}

function isEditorEnabled(): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  if (params.get("focus") === "edit") {
    localStorage.setItem(ENABLE_KEY, "1");
    return true;
  }
  if (params.get("focus") === "off") {
    localStorage.removeItem(ENABLE_KEY);
    return false;
  }
  return localStorage.getItem(ENABLE_KEY) === "1";
}

/**
 * Attempts to detect the primary face in an image and return its centre as
 * normalized (0-100) coordinates relative to the image's natural dimensions.
 *
 * Uses the browser's native FaceDetector API when available (Chromium-based
 * browsers behind a flag, plus some mobile browsers). Returns null on any
 * failure so callers can fall back to defaults / saved values.
 */
async function detectFaceFocal(src: string): Promise<Focal | null> {
  if (typeof window === "undefined") return null;
  const FD = (window as unknown as { FaceDetector?: new (opts?: unknown) => { detect: (img: ImageBitmapSource) => Promise<Array<{ boundingBox: DOMRectReadOnly }>> } }).FaceDetector;
  if (!FD) return null;
  try {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.decoding = "async";
    img.src = src;
    await img.decode();
    const detector = new FD({ fastMode: true, maxDetectedFaces: 1 });
    const faces = await detector.detect(img);
    if (!faces || faces.length === 0) return null;
    const box = faces[0].boundingBox;
    const cx = box.x + box.width / 2;
    const cy = box.y + box.height / 2;
    const x = Math.max(0, Math.min(100, (cx / img.naturalWidth) * 100));
    // Bias the focal slightly above the face centre so the head has breathing
    // room above it instead of being cut at the hairline.
    const y = Math.max(0, Math.min(100, (cy / img.naturalHeight) * 100 - 8));
    return { x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 };
  } catch {
    return null;
  }
}

interface Props {
  src: string;
  alt: string;
  initials?: string;
}

export const FocalPointPortrait = ({ src, alt, initials = "DT" }: Props) => {
  const [state, setState] = useState<StoredState>(INITIAL_STATE);
  const [editorOn, setEditorOn] = useState(false);
  const [activeBp, setActiveBp] = useState<Breakpoint>("mobile");
  const [imgError, setImgError] = useState(false);
  const [autoStatus, setAutoStatus] = useState<"idle" | "detecting" | "found" | "unsupported" | "no-face">("idle");
  const frameRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);

  const focals = state.values;

  // Load on mount + run face detection for any auto breakpoints
  useEffect(() => {
    const loaded = loadState();
    setState(loaded);
    setEditorOn(isEditorEnabled());
    if (typeof window !== "undefined") {
      setActiveBp(window.matchMedia("(min-width: 768px)").matches ? "desktop" : "mobile");
    }

    const needsAuto = loaded.auto.mobile || loaded.auto.desktop;
    if (!needsAuto) return;

    let cancelled = false;
    setAutoStatus("detecting");
    detectFaceFocal(src).then((focal) => {
      if (cancelled) return;
      if (!focal) {
        // FaceDetector unavailable or no face found
        const FD = (window as unknown as { FaceDetector?: unknown }).FaceDetector;
        setAutoStatus(FD ? "no-face" : "unsupported");
        return;
      }
      setAutoStatus("found");
      setState((prev) => {
        const next = { ...prev, values: { ...prev.values } };
        if (prev.auto.mobile) next.values.mobile = focal;
        if (prev.auto.desktop) next.values.desktop = focal;
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [src]);

  // Persist
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      /* ignore quota errors */
    }
  }, [state]);

  const updateFromEvent = useCallback(
    (clientX: number, clientY: number) => {
      const el = frameRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
      const y = Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100));
      setState((prev) => ({
        values: {
          ...prev.values,
          [activeBp]: { x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 },
        },
        // User dragged — this breakpoint is now manual.
        auto: { ...prev.auto, [activeBp]: false },
      }));
    },
    [activeBp],
  );

  const onPointerDown = (e: React.PointerEvent) => {
    if (!editorOn) return;
    draggingRef.current = true;
    (e.target as Element).setPointerCapture?.(e.pointerId);
    updateFromEvent(e.clientX, e.clientY);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!editorOn || !draggingRef.current) return;
    updateFromEvent(e.clientX, e.clientY);
  };
  const onPointerUp = (e: React.PointerEvent) => {
    draggingRef.current = false;
    (e.target as Element).releasePointerCapture?.(e.pointerId);
  };

  // Reset clears manual values and re-runs face detection so "best fit" is restored.
  const reset = () => {
    setState({ values: DEFAULTS, auto: { mobile: true, desktop: true } });
    setAutoStatus("detecting");
    detectFaceFocal(src).then((focal) => {
      if (!focal) {
        const FD = (window as unknown as { FaceDetector?: unknown }).FaceDetector;
        setAutoStatus(FD ? "no-face" : "unsupported");
        return;
      }
      setAutoStatus("found");
      setState({ values: { mobile: focal, desktop: focal }, auto: { mobile: true, desktop: true } });
    });
  };
  const disable = () => {
    localStorage.removeItem(ENABLE_KEY);
    setEditorOn(false);
  };

  // Build CSS variables for both breakpoints. Tailwind reads them via arbitrary values.
  const styleVars = {
    "--focal-mobile": `${focals.mobile.x}% ${focals.mobile.y}%`,
    "--focal-desktop": `${focals.desktop.x}% ${focals.desktop.y}%`,
  } as React.CSSProperties;

  const activeFocal = focals[activeBp];

  return (
    <div className="relative" style={styleVars}>
      <div className="absolute -inset-4 bg-accent/10 blur-3xl rounded-full pointer-events-none" />

      <div
        ref={frameRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        className={`relative w-full overflow-hidden rounded-sm shadow-2xl bg-muted aspect-[3/4] sm:aspect-[4/5] lg:aspect-[2/3] ${
          editorOn ? "cursor-crosshair select-none touch-none" : ""
        }`}
      >
        {/* Placeholder behind image */}
        <div
          aria-hidden="true"
          className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-muted to-secondary/40"
        >
          <span className="text-4xl font-bold tracking-tight text-muted-foreground/50">
            {initials}
          </span>
        </div>

        {!imgError && (
          <img
            src={src}
            alt={alt}
            loading="lazy"
            decoding="async"
            draggable={false}
            onError={() => setImgError(true)}
            className="relative w-full h-full grayscale object-contain object-bottom"
          />
        )}

        {/* Editor overlay */}
        {editorOn && (
          <>
            {/* Crosshair marker for the active breakpoint */}
            <div
              className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-1/2"
              style={{ left: `${activeFocal.x}%`, top: `${activeFocal.y}%` }}
            >
              <div className="w-6 h-6 rounded-full border-2 border-accent-mint bg-accent-mint/20 shadow-[0_0_0_2px_rgba(0,0,0,0.5)]" />
              <div className="absolute left-1/2 top-1/2 w-px h-10 -translate-x-1/2 -translate-y-1/2 bg-accent-mint/70" />
              <div className="absolute left-1/2 top-1/2 h-px w-10 -translate-x-1/2 -translate-y-1/2 bg-accent-mint/70" />
            </div>

            {/* Ghost marker for the other breakpoint, for reference */}
            {(["mobile", "desktop"] as Breakpoint[])
              .filter((b) => b !== activeBp)
              .map((b) => (
                <div
                  key={b}
                  className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-1/2"
                  style={{ left: `${focals[b].x}%`, top: `${focals[b].y}%` }}
                >
                  <div className="w-3 h-3 rounded-full border border-white/70 bg-black/40" />
                </div>
              ))}
          </>
        )}
      </div>

      {/* Editor controls (dev only) */}
      {editorOn && (
        <div className="mt-3 p-3 rounded-sm border border-accent/40 bg-background/95 backdrop-blur text-xs font-mono space-y-2 shadow-md">
          <div className="flex items-center justify-between gap-2">
            <span className="uppercase tracking-widest text-accent">Focal editor</span>
            <button
              type="button"
              onClick={disable}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Hide focal editor"
            >
              hide
            </button>
          </div>
          <div className="flex gap-2">
            {(["mobile", "desktop"] as Breakpoint[]).map((b) => (
              <button
                key={b}
                type="button"
                onClick={() => setActiveBp(b)}
                className={`px-3 py-1.5 border tracking-wide ${
                  activeBp === b
                    ? "border-accent bg-accent text-accent-foreground"
                    : "border-border bg-background hover:border-accent/60"
                }`}
              >
                {b}
              </button>
            ))}
            <button
              type="button"
              onClick={reset}
              className="ml-auto px-3 py-1.5 border border-border hover:border-destructive hover:text-destructive"
            >
              reset
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2 text-muted-foreground">
            <div>
              mobile: <span className="text-foreground">{focals.mobile.x}% {focals.mobile.y}%</span>
              {state.auto.mobile && <span className="ml-1 text-accent-mint">auto</span>}
            </div>
            <div>
              desktop: <span className="text-foreground">{focals.desktop.x}% {focals.desktop.y}%</span>
              {state.auto.desktop && <span className="ml-1 text-accent-mint">auto</span>}
            </div>
          </div>
          <div className="text-[10px] text-muted-foreground/80">
            face-detect:{" "}
            <span className="text-foreground">
              {autoStatus === "found" && "✓ centered on face"}
              {autoStatus === "detecting" && "scanning…"}
              {autoStatus === "no-face" && "no face found, using defaults"}
              {autoStatus === "unsupported" && "FaceDetector unsupported, using defaults"}
              {autoStatus === "idle" && "—"}
            </span>
          </div>
          <p className="text-[10px] text-muted-foreground/70 leading-relaxed">
            Drag on the photo to set the focus for the active breakpoint. Saved to this browser only.
            Append <span className="text-foreground">?focus=off</span> to the URL to fully disable.
          </p>
        </div>
      )}
    </div>
  );
};

export default FocalPointPortrait;
